import _ from 'lodash'
import connectedAuthWrapper from 'redux-auth-wrapper/connectedAuthWrapper'

import { evalEnv } from '../../lib/renderUtil'
import { getLogger } from '../../lib/browserLogger'
import confUtil from '../../lib/confUtil'
import NoPrivilegedPermission from '../../app/components/Common/NoPrivilegedPermission'
import NoSupplierPermission from '../../app/components/Common/NoSupplierPermission'
import { getFeatureToggle } from '@vzmi/component_ec_nodejs_util/dist/lib/FeatureToggleUtil'

const logger = getLogger({ name: 'MainComponent' })

export const isPathInPrivilegedPages = (path, privilegedPages) => _.some(privilegedPages, { path })

const verifyPrivilegedPages = (state, props, opts = {}) => {
  const {
    path,
    bypassOnProduction = false,
    isHashNeeded = false
  } = opts

  if (!state.page) {
    logger.error('No page found, please check for your redux container props mapping. state: ', state)
    return false
  }

  const {
    privilegedPages,
    env
  } = state.page

  const { isProduction } = evalEnv(env)

  // APACSMDESK-19796： Bypass verifying on production
  if (bypassOnProduction && isProduction) {
    return true
  }

  const location = props.location || (global.window ? window.location : {})
  const { pathname } = location
  // hash might not be accurate, add pathname as a fallback
  const localPath = (
    path ||
    (
      isHashNeeded
        ? _.replace(pathname, /^\//, '/#')
        : pathname
    )
  )

  return _.some(privilegedPages, { path: localPath })
}

/**
 * 只在 DEV 環境下可以看到的頁面
 */

const verifyDevPrivileged = (state, props) => {
  const env = _.get(state, 'page.env', '')
  const { isProduction } = evalEnv(env)

  if (isProduction) {
    return false
  }

  const location = props.location || (global.window ? window.location : {})
  const { pathname } = location

  const DEV_PRIVILEGED_PATH = [
    // Add paths allowing privilegedPages for dev usage if necessary
    // '/proposal/updateAttribute'
  ]
  return DEV_PRIVILEGED_PATH.some(path => pathname.startsWith(path))
}

/**
 * 確認是否有目前路徑或特定路徑權限
 * @param {Object} state
 * @param {Object} props
 * @param {Object} param2
 * @param {Array} param2.paths urlConf 中的特定路徑
 */
export const verifyPrivilegedPagesWithFallbackPaths = (state, props, opts) => {
  const {
    paths = [],
    allPathsMatched
  } = opts
  const isCurrentPathPrivileged = verifyPrivilegedPages(state, props)

  const checkPathFunction = allPathsMatched ? _.every : _.some
  const isFallbackPathsPrivileged = checkPathFunction(
    paths,
    path => verifyPrivilegedPages(state, props, { path: confUtil.getUrl(path) })
  )

  const isDevPrivileged = verifyDevPrivileged(state, props)

  const isVerifiedSuccess = _.some([
    isCurrentPathPrivileged,
    isFallbackPathsPrivileged,
    isDevPrivileged
  ])

  return isVerifiedSuccess
}

// 驗證 PM 登入的使用者是否具有該供應商權限
// https://jira.vzbuilders.com/browse/APACSMDESK-56812
export const verifySupplierPermission = (state, props) => {
  const {
    isCm,
    identity,
    supplierId,
    grantedSupplierIds = []
  } = _.get(state, 'page.user', {})
  const flags = _.get(state, 'flags', {})
  const kaFeatureToggle = getFeatureToggle(flags, 'sp_KA')
  const isLoginWithPM = _.get(identity, 'isPM')
  // 只有 PM 需要驗證
  const isVerifiedSuccess = (kaFeatureToggle && isCm && isLoginWithPM)
    ? _.some(grantedSupplierIds, id => id === supplierId)
    : true
  return isVerifiedSuccess
}

/**
 * HOC
 * @param {Component} PrivilegedComponent
 * @param {Array} fallbackPaths urlConf 中的特定路徑
 */
export const privilegedOrWithinFallbackPath = (PrivilegedComponent, fallbackPaths, allPathsMatched = false, FailedComponent = NoPrivilegedPermission) => {
  const opts = { paths: fallbackPaths, allPathsMatched }
  return privilegedPagesProtected({
    Component: PrivilegedComponent,
    FailureComponent: () => (
      <FailedComponent
        {...opts}
      />
    ),
    customizedAuthorityStrategy: verifyPrivilegedPagesWithFallbackPaths,
    opts
  })
}

export const withSupplierPermission = (PrivilegedComponent, fallbackPaths) => {
  return privilegedPagesProtected({
    Component: PrivilegedComponent,
    FailureComponent: NoSupplierPermission,
    customizedAuthorityStrategy: verifySupplierPermission,
    opts: { paths: fallbackPaths }
  })
}

const DefaultFailureComponent = () => null

/** use by privilegedPages protected component */
export const privilegedPagesProtected = params => {
  const {
    opts,
    Component,
    FailureComponent,
    customizedAuthorityStrategy
  } = params

  const authorize = customizedAuthorityStrategy || verifyPrivilegedPages

  /**
  * redux-auth-wrapper
  * ref: https://mjrussell.github.io/redux-auth-wrapper/docs/Getting-Started/HidingAlternate.html
  */
  return connectedAuthWrapper({
    wrapperDisplayName: 'PrivilegePagesProtected',
    authenticatedSelector: (state, props) => authorize(state, props, opts),
    FailureComponent: FailureComponent || DefaultFailureComponent
  })(Component)
}
